import isPlainObject from "lodash.isplainobject";
import { isObject } from "./is-object";
import { isNumber } from "./null-checks";

type CleanOptions = {
    maxDepth?: number;
    depth?: number;
    excludeNull?: boolean;
    excludeArrays?: boolean;
};

/**
 * Removes undefined properties from object recursively
 * @param {*} object
 * @returns
 */
const clean = <T>(object: T, options?: CleanOptions): Partial<T> => {
    const innerClean = (
        object: T,
        options?: CleanOptions,
    ): Partial<T> | undefined => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const _object = object as any;
        const { depth, maxDepth, excludeNull, excludeArrays } = {
            excludeNull: false,
            depth: 0,
            maxDepth: Infinity,
            excludeArrays: false,
            ...options,
        } as CleanOptions;

        const cleanValue = (propertyName: string) => {
            // Property is undefined
            if (_object[propertyName] === "undefined") {
                delete _object[propertyName];
                return;
            }

            // Exclude null is true, and property has value null..
            if (!!excludeNull && _object[propertyName] === null) {
                delete _object[propertyName];
                return;
            }
        };

        // Are we to deep in yet?
        if (isNumber(depth) && isNumber(maxDepth) && maxDepth <= depth)
            return undefined;

        // Are we an object or an array?
        switch (Array.isArray(_object)) {
            case true:
                return excludeArrays
                    ? _object
                    : // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      _object.map((obj: any) => innerClean(obj, options));
            case false: {
                for (const key of Object.keys(_object)) {
                    if (key in _object && isObject(_object[key])) {
                        const _key = key as keyof typeof _object;
                        innerClean(_object[_key], {
                            ...options,
                            depth: (depth ?? 0) + 1,
                        });
                    } else {
                        if (Array.isArray(_object[key])) {
                            if (!options?.excludeArrays) {
                                _object[key]
                                    .map(item =>
                                        isPlainObject(item)
                                            ? clean(item)
                                            : item,
                                    )
                                    .filter(
                                        item =>
                                            !["undefined", "null"].includes(
                                                typeof item,
                                            ),
                                    );
                            }
                        } else {
                            cleanValue(key);
                        }
                    }
                }
                break;
            }
        }
        return _object;
    };

    const output = innerClean(object, options);
    if (typeof output === "undefined") {
        // eslint-disable-next-line no-console
        console.error("CLEAN FAIL", { object, output });
        throw new Error("Clean operation returned undefined, can't do that");
    }

    return output;
};

/**
 * Removes undefined and null values from the given object.
 * (Not nullish values. Only undefined and null)
 * This function performs a shallow clean, meaning it only removes undefined values from the top level of the object.
 * Nested objects are not modified.
 *
 * @param input - The object to clean.
 * @returns A new object with undefined and null values removed.
 * @typeParam T - The type of the object.
 */
const shallowCleanObject = <T extends Record<string, unknown>>(
    input: T,
    options?: {
        /** Do NOT remove props with `null` as value when `true` */
        excludeNull?: boolean;
        /** Do NOT remove props with `undefined` as value when `true` */
        excludeUndefined?: boolean;
    },
): Partial<T> => {
    if (options?.excludeNull && options?.excludeUndefined) return input;
    const object = { ...input };
    for (const key in object) {
        if (!options?.excludeUndefined && typeof object[key] === "undefined")
            delete object[key];
        if (!options?.excludeNull && object[key] === null) delete object[key];
    }
    return object as Partial<T>;
};

/**
 * Removes null values from the given object.
 * This function performs a shallow clean, meaning it only removes null values from the top level of the object.
 * Nested objects are not modified.
 *
 * @param input - The object to clean.
 * @returns A new object with null values removed.
 * @typeParam T - The type of the object.
 */
const shallowCleanObjectFromNulls = <T extends Record<string, unknown>>(
    input: T,
): Partial<T> => shallowCleanObject(input, { excludeUndefined: true });
/**
 * Removes null values from the given object.
 * This function performs a shallow clean, meaning it only removes null values from the top level of the object.
 * Nested objects are not modified.
 *
 * @param input - The object to clean.
 * @returns A new object with null values removed.
 * @typeParam T - The type of the object.
 */
const shallowCleanObjectFromUndefineds = <T extends Record<string, unknown>>(
    input: T,
): Partial<T> => shallowCleanObject(input, { excludeNull: true });

export {
    clean,
    shallowCleanObject,
    shallowCleanObjectFromNulls,
    shallowCleanObjectFromUndefineds,
};
